import {
  IResourceComponentsProps,
  useCustom,
  useUpdate,
} from "@refinedev/core";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import { useState, useMemo } from "react";
import { IInvestment } from "interfaces/investment";
import { Show } from "@refinedev/mui";
import { UpdateInvestmentCard } from "components/card/UpdateInvestmentCard";
import { getLast7Dates } from "utils/date-format";
import { useNavigate } from "react-router-dom";
import { UnlockInvestmentConfirmationModal } from "components/modal/unlock-investment-confirmation-modal";

export const BatchUpdate: React.FC<IResourceComponentsProps> = () => {
  const last7Dates = getLast7Dates();
  const navigate = useNavigate();
  const [showZeroAmount, setShowZeroAmount] = useState(false);
  const {
    data: investments,
    isSuccess: isGetInvestmentSuccess,
    refetch,
  } = useCustom({
    url: `investments-with-history`,
    method: "get",
  });
  const { data, refetch: reload } = useCustom({
    url: `investments-with-history/locked`,
    method: "get",
  });
  const { mutate, isLoading } = useUpdate();
  const [updatedInvestment, setUpdatedInvestment] = useState<
    {
      ticker: string;
      id: number;
      marketPrice: number;
      dailyChange: number;
      date: Date;
    }[]
  >([]);
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [selectedUnlockDate, setSelectedUnlockDate] = useState<string>("");

  const updateInvestment = (
    ticker: string,
    id: number,
    marketPrice: number,
    dailyChange: number,
    date: Date
  ) => {
    setUpdatedInvestment((prev) => {
      const newData = {
        ticker,
        id,
        marketPrice,
        dailyChange,
        date,
      };
      let updated = prev;
      const index = updated.findIndex((item) => item.id === id);
      if (index === -1) {
        updated.push(newData);
      } else {
        updated[index] = newData;
      }
      return updated;
    });
  };
  const submit = () => {
    mutate(
      {
        resource: "investments-batch-update",
        values: { input: updatedInvestment },
        id: "",
      },
      {
        onSuccess: () => {
          refetch();
        },
      }
    );
  };
  const lock = (date: string) => {
    mutate(
      {
        resource: "investments-history-lock",
        values: { date: date },
        id: "",
      },
      {
        onSuccess: () => {
          refetch();
          reload();
        },
      }
    );
  };
  const canLockDay = (date: string) => {
    if (data && data.data) {
      const lastDay = new Date(data?.data as any);
      lastDay.setDate(lastDay.getDate() + 1);
      return date === lastDay.toISOString().split("T")[0];
    } else return false;
  };

  // Get all locked dates from investments data, sorted by date (newest first)
  const lockedDates = useMemo(() => {
    if (!investments?.data?.data) return [];
    const dates = new Set<string>();
    investments.data.data.forEach((investment: IInvestment) => {
      investment.investmentMarketPriceHistory?.forEach((history) => {
        if (history.locked) {
          const dateStr = new Date(history.referenceDate)
            .toISOString()
            .split("T")[0];
          dates.add(dateStr);
        }
      });
    });
    // Sort dates descending (newest/most recent first)
    return Array.from(dates).sort((a, b) => {
      return new Date(b).getTime() - new Date(a).getTime();
    });
  }, [investments?.data?.data]);

  // Get the most recent locked date (last locked date - the one that should be unlocked first)
  const mostRecentLockedDate = useMemo(() => {
    if (lockedDates.length === 0) return null;
    return lockedDates[0]; // First item is the most recent (newest) locked date
  }, [lockedDates]);

  // Check if a date is locked
  const isDateLocked = (date: string) => {
    return lockedDates.includes(date);
  };

  // Check if a date can be unlocked (only the most recent locked date can be unlocked)
  // After unlocking, the next most recent date becomes unlockable
  const canUnlockDay = (date: string) => {
    if (!mostRecentLockedDate) return false;
    return mostRecentLockedDate === date;
  };

  const handleUnlockClick = (date: string) => {
    setSelectedUnlockDate(date);
    setUnlockModalOpen(true);
  };

  const handleUnlockSuccess = () => {
    refetch();
    reload();
  };

  return (
    <Show
      title={""}
      headerButtons={() => (
        <>
          <Button
            color="primary"
            onClick={() => navigate("/fund/log/snapshot")}
            variant="contained"
          >
            View snapshot
          </Button>
        </>
      )}
    >
      <Box sx={{ marginY: 4, width: "fit-content" }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={showZeroAmount}
              onChange={(e) => setShowZeroAmount(e.target.checked)}
              color="primary"
            />
          }
          label="Show investments with zero units"
        />

        {isGetInvestmentSuccess && data && (
          <>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "none",
                maxWidth: 1150,
                overflowX: "auto",
                border: "1px solid",
              }}
            >
              <Table size="small" sx={{ width: "inherit", display: "block" }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        backgroundColor: "white",
                        minWidth: 100,
                        border: "1px solid rgba(224, 224, 224, 1);",
                      }}
                    >
                      Ticker
                    </TableCell>
                    {last7Dates.map((date) => (
                      <TableCell
                        key={date}
                        sx={{
                          minWidth: 150,
                          border: "1px solid rgba(224, 224, 224, 1);",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          {date}
                          {isDateLocked(date) ? (
                            canUnlockDay(date) ? (
                              <IconButton
                                onClick={() => handleUnlockClick(date)}
                                edge="end"
                                color="primary"
                                title="Unlock this date"
                              >
                                <LockIcon />
                              </IconButton>
                            ) : (
                              <IconButton
                                edge="end"
                                disabled
                                title="Unlock previous dates first"
                              >
                                <LockIcon />
                              </IconButton>
                            )
                          ) : (
                            <IconButton
                              onClick={() => lock(date)}
                              edge="end"
                              color="primary"
                              disabled={!canLockDay(date)}
                              title={
                                canLockDay(date)
                                  ? "Lock this date"
                                  : "Lock previous dates first"
                              }
                            >
                              <LockOpenIcon />
                            </IconButton>
                          )}
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody sx={{ width: "inherit", display: "block" }}>
                  {investments.data.data &&
                    investments.data.data.length > 0 &&
                    investments.data.data
                      .filter(
                        (investment: IInvestment) =>
                          showZeroAmount ||
                          (investment.investmentMarketPriceHistory &&
                            investment.investmentMarketPriceHistory[0]
                              .netaTotalApprovedUnits !== 0)
                      )
                      .map((investment: IInvestment, index: number) => (
                        <UpdateInvestmentCard
                          key={investment.ticker}
                          investment={investment}
                          updateInvestment={updateInvestment}
                        />
                      ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ my: 4, display: "flex", justifyContent: "flex-end" }}>
              <Button
                sx={{ mr: 2 }}
                startIcon={
                  isLoading && <CircularProgress size={20} color="inherit" />
                }
                disabled={isLoading}
                variant="contained"
                onClick={() => {
                  submit();
                }}
              >
                Confirm
              </Button>
              <Button
                variant="outlined"
                onClick={() => {
                  navigate(-1);
                }}
              >
                Close
              </Button>
            </Box>
          </>
        )}
      </Box>
      <UnlockInvestmentConfirmationModal
        open={unlockModalOpen}
        setOpen={setUnlockModalOpen}
        date={selectedUnlockDate}
        onSuccess={handleUnlockSuccess}
      />
    </Show>
  );
};
